import '../instrumentation' // must be first — Sentry init before everything

// ── CSS imports ───────────────────────────────────────────────────────────────
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/tiptap/styles.css'
import '@mantine/carousel/styles.css'
import '@progress/kendo-theme-material/dist/all.css'
import '../theme/kendo/kendo.css'
import '../theme/material/material-ui.css'
import '../theme/loader.css'
import '../theme/global.css'
import '../theme/time-picker.css'
import '../theme/signature-pad.css'
import '../theme/mantine-overrides.css'
import 'react-dual-listbox/lib/react-dual-listbox.css'
import '../theme/listbox.css'
import '../theme/maps.css'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { RouterProvider } from '@tanstack/react-router'
import { AppErrorFallback } from '@/components/errorfallback/app-error-fallback'
import loggingService from '@/services/logging-service'
import { QueryClientProvider } from '@tanstack/react-query'
import { MantineProvider, createTheme } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { Provider as JotaiProvider } from 'jotai'
import { queryClient } from './query-client'
import { mantineTheme } from '@/theme/mantineTheme'
import { router } from './router'

const theme = createTheme(mantineTheme)

// Reports a crash to the backend without rendering an error page; re-homed from the
// deleted pages/_app.tsx, whose Sentry boundary carried this wiring.
const CrashReporter = ({ error, componentStack }: { error?: unknown; componentStack?: string }) => {
  React.useEffect(() => {
    loggingService.logClientCrash(error, componentStack)
  }, [])
  return <></>
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* Catches catastrophic errors outside the router (providers, theme setup).
        Reported but not shown — route-level errors are captured in RouterErrorFallback
        via Sentry.captureException without showing a UI. Re-enable AppErrorFallback
        once the error noise is tackled. */}
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack }) => (
        <CrashReporter error={error} componentStack={componentStack} />
      )}
    >
    <JotaiProvider>
      <QueryClientProvider client={queryClient}>
        <MantineProvider theme={theme}>
          <ModalsProvider
            modalProps={{
              size: 'auto',
              withCloseButton: false,
              closeOnEscape: true,
              closeOnClickOutside: true,
              centered: true,
              overlayProps: {
                color: 'var(--mantine-color-scBlue-5)',
                blur: 10,
                opacity: 0.55,
              },
              transitionProps: {
                transition: 'pop',
                exitDuration: 50,
                duration: 100,
                timingFunction: 'ease',
              },
              radius: 'sm',
            }}
          >
            <RouterProvider router={router} />
          </ModalsProvider>
        </MantineProvider>
      </QueryClientProvider>
    </JotaiProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
)
